import _ from 'lodash';
import { LocalDate } from '@js-joda/core';

// models and services
import { AccountModel } from 'src/types/account.model';
import { Board } from 'src/bw/board/Board';
import { BudgetModel } from 'src/types/budget.model';
import { CardModel } from 'src/types/card.model';
import { CategoryModel } from 'src/types/category.model';
import { DebtModel, DebtModelType } from 'src/types/debt.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { InvestmentModel } from 'src/types/investment.model';
import { MatchModel } from 'src/types/match.model';
import { RealEstateModel } from 'src/types/real-estate.model';
import { RecurringExpenseModel } from 'src/types/recurring-expense.model';
import { RuleModel } from 'src/types/rule.model';
import { StatsModel } from 'src/types/stats.model';
import { TransactionModel, TransactionSummary } from 'src/types/transaction.model';
import AccountService from '../../services/account.service';
import BudgetService from '../../services/budget.service';
import CashFlowService from '../../services/cashflow.service';
import CategoryService from '../../services/category.service';
import CryptoService from '../../services/CryptoService';
import FormatService from '../../services/format.service';
import IncomeSourceService from '../../services/income-source.service';
import IncomeSourceRuleService from '../../services/income-source-rule.service';
import PersonService from '../../services/person.service';
import PlanService from '../../services/plan.service';
import MatchService from '../../services/match.service';
import NavigationService from '../../services/navigation.service';
import RealEstateService from '../../services/real-estate.service';
import RecurringExpenseService from '../../services/recurring-expense.service';
import ReportService, { PlotPoint } from '../../services/report.service';
import RuleService from '../../services/rule.service';
import SubRouteService from '../../services/sub-route.service';
import { TimePeriod } from 'src/types/time-period';
import TimeService from '../../services/time.service';
import TransactionService from '../../services/transaction.service';
import CardService from 'src/services/card.service';
import UrlHelper from 'src/utils/url.helper';
export default class RouteFactory {
    private readonly component: Board;

    constructor(component: any) {
        this.component = component;
    }

    private match(route: string, pathname: string): boolean {
        route = route.replaceAll(':number', '[0-9]+'); // any length number
        route = route.replaceAll(':date', '[0-9-]+'); // any length number
        route = route.replaceAll(':string', '[A-Za-z0-9-]+'); // any length word, may have hyphens
        route = route.replaceAll(':boolean', '(true|false)'); // boolean

        return Boolean(pathname.match(new RegExp(`/?${route}/?`, 'g')));
    }

    /**
     * A route can be formed in several ways.
     *
     * Ex 1: this/that/other
     * Ex 2: this/:number
     * Ex 3: this/:string
     * Ex 4: this/that/:number/other/:string
     *
     * Forward slashes before and after the route are handled automatically.
     */
    public async handleRoute(): Promise<void> {
        const pathname = UrlHelper.stripBasePath(window.location.pathname.toString());
        switch (true) {
            case this.match('show/:string/:number', pathname):
                await this.handleShowReport(pathname, false);
                window.dispatchEvent(new Event('resize'));
                break;
            case this.match('show-month/:string/:date', pathname):
                await this.handleShowReport(pathname, true);
                window.dispatchEvent(new Event('resize'));
                break;
            case this.match('create/goal/:string/:number', pathname):
                {
                    const phrases: string[] = pathname.split('/');
                    let months: number = Number(phrases[phrases.length - 1]);
                    let name: string = phrases[phrases.length - 2];
                    let startDate: LocalDate = null;
                    if (isNaN(months)) {
                        months = null;
                        name = phrases[phrases.length - 1];
                        startDate = LocalDate.now().plusMonths(months);
                    }

                    await this.component.cardFactory.handleCreateAndOpenCardFromRoute(
                        {
                            description: name.replaceAll('-', ' ').toLowerCase()
                        },
                        startDate
                    );
                }
                break;
            case this.match('edit/card/:number', pathname):
                {
                    const phrases = pathname.split('/');
                    const id = Number(phrases[phrases.length - 1]);
                    if (!id) {
                        await NavigationService.goTo(this.component.props.history, '/');
                        return;
                    }

                    // these are case-sensitive
                    const attributes: any = SubRouteService.parseQuery(window.location.search);
                    if (!attributes) {
                        await NavigationService.goTo(this.component.props.history, `/card/${id}`);
                        return;
                    }

                    const cards: CardModel[] = _.get(this.component.props, 'user.cards');
                    const card: CardModel = _.find(cards, { id });
                    try {
                        for (const key in attributes) {
                            card.attributes[key] = attributes[key];
                        }
                    } catch (e) {
                        console.error(e);
                        await NavigationService.goTo(this.component.props.history, `/card/${id}`);
                        return;
                    }

                    await this.component.cardFactory.handleUpdateCard(card);
                    await NavigationService.goTo(this.component.props.history, `/card/${id}`);
                }
                break;
            case this.match('card/:number', pathname):
                {
                    const cards: CardModel[] = _.get(this.component.props, 'user.cards');
                    const id: number = Number(pathname.split('/').pop());
                    const card: CardModel = _.find(cards, { id });
                    if (!card) {
                        await NavigationService.goTo(this.component.props.history, '/');
                        return;
                    }
                    await this.component.cardFactory.handleOpenCardFromRoute(card);
                }
                break;
            case this.match('add-accounts/cash-and-banking', pathname):
                {
                    this.component.setState({
                        drawerState: 'add-accounts/cash-and-banking'
                    });
                }
                break;
            case this.match('add-accounts/investments-and-retirement', pathname):
                {
                    this.component.setState({
                        drawerState: 'add-accounts/investments-and-retirement'
                    });
                }
                break;
            case this.match('add-accounts/property-and-assets', pathname):
                {
                    this.component.setState({
                        drawerState: 'add-accounts/property-and-assets'
                    });
                }
                break;
            case this.match('add-accounts/loans-and-debt', pathname):
                {
                    this.component.setState({
                        drawerState: 'add-accounts/loans-and-debt'
                    });
                }
                break;
            case this.match('debt/:number', pathname):
                {
                    const debts: DebtModel[] = _.get(this.component.props, 'user.debts');
                    const id: number = Number(pathname.split('/').pop());
                    const debt: DebtModel = _.find(debts, { id });
                    if (!debt) {
                        await NavigationService.goTo(this.component.props.history, '/');
                        return;
                    }
                    this.component.setState({
                        drawerState: 'debt',
                        drawerProps: {
                            currentDebt: debt
                        }
                    });
                }
                break;
            case this.match('debt/:string', pathname):
                {
                    const debtType: string = pathname.split('/').pop();
                    this.component.setState({
                        drawerState: 'debt',
                        drawerProps: {
                            currentDebt:
                                debtType in DebtModelType
                                    ? {
                                          type: debtType,
                                          name: debtType
                                      }
                                    : null
                        }
                    });
                }
                break;
            case this.match('debt', pathname):
                {
                    this.component.setState({
                        drawerState: 'debt',
                        drawerProps: {
                            currentDebt: null
                        }
                    });
                }
                break;
            case this.match('realestate/:number', pathname):
                {
                    const properties: RealEstateModel[] = await RealEstateService.getRealEstate();
                    const id: number = Number(pathname.split('/').pop());
                    const property: RealEstateModel = _.find(properties, { id });
                    if (!property) {
                        await NavigationService.goTo(this.component.props.history, '/');
                        return;
                    }
                    this.component.setState({
                        drawerState: 'real-estate',
                        drawerProps: {
                            currentRealEstate: property
                        }
                    });
                }
                break;
            case this.match('crypto/:number', pathname):
                {
                    const investments: InvestmentModel[] = _.get(this.component.props, 'user.holdings');
                    const id: number = Number(pathname.split('/').pop());
                    const investment: InvestmentModel = _.find(investments, { id });
                    if (!investment) {
                        await NavigationService.goTo(this.component.props.history, '/');
                        return;
                    }
                    this.component.setState({
                        drawerState: 'crypto',
                        drawerProps: {
                            crypto: await CryptoService.get(),
                            currentInvestment: investment
                        }
                    });
                }
                break;
            case this.match('crypto', pathname):
                this.component.setState({
                    drawerState: 'crypto',
                    drawerProps: {
                        crypto: await CryptoService.get(),
                        currentInvestment: null
                    }
                });
                break;
            case this.match('investment/:number', pathname):
                {
                    const investments: InvestmentModel[] = _.get(this.component.props, 'user.holdings');
                    const id: number = Number(pathname.split('/').pop());
                    const investment: InvestmentModel = _.find(investments, { id });
                    if (!investment) {
                        await NavigationService.goTo(this.component.props.history, '/');
                        return;
                    }
                    this.component.setState({
                        drawerState: 'investment',
                        drawerProps: {
                            currentInvestment: investment
                        }
                    });
                }
                break;
            case this.match('investment/:string', pathname):
                const type: string = pathname.split('/').pop();
                this.component.setState({
                    drawerState: 'investment',
                    drawerProps: {
                        currentInvestment: null,
                        type
                    }
                });
                break;
            case this.match('investment', pathname):
                this.component.setState({
                    drawerState: 'investment',
                    drawerProps: {
                        currentInvestment: null
                    }
                });
                break;

            case this.match('plan', pathname):
                {
                    const offset: number = Math.max(
                        0,
                        CashFlowService.getCashFlowIndexWithDate(
                            this.component.props.user.rawCashFlow,
                            LocalDate.now()
                        ) - 1
                    );
                    const startDate = this.component.props.user.rawCashFlow[offset].startDate;
                    const endDate = this.component.props.user.rawCashFlow[offset].endDate;
                    const summaries: TransactionSummary[] = await TransactionService.getTransactionSummaries(
                        startDate,
                        endDate
                    );

                    this.component.setState({
                        drawerState: 'plan',
                        drawerProps: {
                            summaries
                        }
                    });
                }
                break;
            case this.match('insights', pathname):
                {
                    const offset: number = Math.max(
                        0,
                        CashFlowService.getCashFlowIndexWithDate(
                            this.component.props.user.rawCashFlow,
                            LocalDate.now()
                        ) - 1
                    );
                    const startDate = this.component.props.user.rawCashFlow[offset].startDate;
                    const endDate = this.component.props.user.rawCashFlow[offset].endDate;
                    const summary = await BudgetService.getSummary();
                    const summaries: TransactionSummary[] = await TransactionService.getTransactionSummaries(
                        startDate,
                        endDate
                    );
                    const stats: StatsModel = await PlanService.getSummary();
                    const cards: CardModel[] = _.get(this.component.props, 'user.cards');
                    const savingsGoalsCards: CardModel[] = CardService.getSavingsGoalCards(cards);

                    this.component.setState({
                        drawerState: 'insight',
                        drawerProps: {
                            summary,
                            summaries,
                            budgets: await BudgetService.getBudget(),
                            stats,
                            savingsGoalsCards
                        }
                    });
                }
                break;
            case this.match('budget/history/:string', pathname):
                {
                    if (!this.component.props.user.rawCashFlow) {
                        return;
                    }

                    const endOffset = Math.max(
                        CashFlowService.getCashFlowIndexWithDate(
                            this.component.props.user.rawCashFlow,
                            LocalDate.now()
                        ) - 1,
                        0
                    );
                    const startOffset = Math.max(endOffset - 11, 0);
                    const startDate = this.component.props.user.rawCashFlow[startOffset].startDate;
                    const endDate = this.component.props.user.rawCashFlow[endOffset].endDate;

                    const category = pathname.split('/')[3];
                    const budgets = await BudgetService.getBudget();

                    if (!_.find(budgets, { groupId: category })) {
                        await NavigationService.goTo(this.component.props.history, '/budget/');
                        return;
                    }

                    this.component.setState({
                        drawerState: 'budget-history',
                        drawerProps: {
                            startOffset,
                            endOffset,
                            startDate,
                            endDate,
                            category,
                            budgets
                        }
                    });
                }
                break;
            case this.match('budget/start', pathname):
                {
                    if (!this.component.props.user.rawCashFlow) {
                        return;
                    }
                    const offset: number = Math.max(
                        0,
                        CashFlowService.getCashFlowIndexWithDate(this.component.props.user.rawCashFlow, LocalDate.now())
                    );
                    const startOffset = Math.max(0, offset - 12);
                    const months = offset - startOffset;

                    const budgets: BudgetModel[] = await BudgetService.getSuggested();

                    // The BudgetAdjustDrawer uses tempId to keep track of budgets because they more or may not have a
                    // groupId yet
                    let tempId = 1;
                    for (const budget of budgets) {
                        budget.tempId = tempId++;
                    }

                    const categories = await CategoryService.getCategories();

                    this.component.setState({
                        drawerState: 'budget-adjust',
                        drawerProps: {
                            budgets,
                            categories,
                            months,
                            tempId
                        }
                    });
                }
                break;
            case this.match('budget', pathname):
                {
                    const budgets: BudgetModel[] = await BudgetService.getBudget();
                    if (!budgets.length) {
                        await NavigationService.goTo(this.component.props.history, '/budget/start');
                        return;
                    }

                    let offset: number = Math.max(
                        0,
                        CashFlowService.getCashFlowIndexWithDate(
                            this.component.props.user.rawCashFlow,
                            LocalDate.now()
                        ) - 1
                    );
                    if (!this.component.props.user.rawCashFlow) {
                        return;
                    }
                    let startDate: LocalDate = this.component.props.user.rawCashFlow[offset].startDate;
                    let endDate: LocalDate = this.component.props.user.rawCashFlow[offset].endDate;
                    const summaries: TransactionSummary[] = await TransactionService.getTransactionSummaries(
                        startDate,
                        endDate
                    );

                    offset += 1;
                    startDate = this.component.props.user.rawCashFlow[offset].startDate;
                    endDate = this.component.props.user.rawCashFlow[offset].endDate;

                    const categories = await CategoryService.getCategories();

                    this.component.setState({
                        drawerState: 'global-budget',
                        drawerProps: {
                            summaries,
                            thisMonth: await TransactionService.getTransactionSummaries(startDate, endDate),
                            categories,
                            handleBack: async () => await NavigationService.goTo(this.component.props.history, '/'),
                            budgets
                        }
                    });
                }
                break;
            case this.match('goals', pathname):
                const incomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();

                this.component.setState({
                    drawerState: 'goals',
                    drawerProps: {
                        incomeSources,
                        user: this.component.props.user,
                        handleRefresh: this.component.handleReloadUser.bind(this.component),
                        handleBack: async () => await NavigationService.goTo(this.component.props.history, '/')
                    }
                });
                break;
            case this.match('timeline', pathname):
                this.component.setState({
                    drawerState: 'timeline',
                    drawerProps: {
                        user: this.component.props.user,
                        handleRefresh: this.component.handleReloadUser.bind(this.component),
                        handleBack: async () => await NavigationService.goTo(this.component.props.history, '/')
                    }
                });
                break;
            case this.match('/drawer/incomes/:date/:date', pathname):
                {
                    const startDate: LocalDate = LocalDate.parse(pathname.split('/')[3]);
                    const endDate: LocalDate = LocalDate.parse(pathname.split('/')[4]);
                    await this.component.incomeFactory.openIncomeSummaryRoute(startDate, endDate);
                }
                break;
            case this.match('/drawer/expenses/:date/:date', pathname):
                {
                    const startDate: LocalDate = LocalDate.parse(pathname.split('/')[3]);
                    const endDate: LocalDate = LocalDate.parse(pathname.split('/')[4]);
                    await this.component.expensesFactory.openExpensesSummaryRoute(startDate, endDate);
                }
                break;
            case this.match('/drawer/expense/:number', pathname):
                {
                    const phrases: string[] = pathname.split('/');
                    const id: number = Number(phrases[phrases.length - 1]);
                    const recurringExpenses: RecurringExpenseModel[] =
                        await RecurringExpenseService.getRecurringExpenses();
                    const recurringExpense = _.find(recurringExpenses, { id });
                    if (recurringExpense) {
                        this.component.setState({
                            drawerState: 'expense',
                            drawerProps: {
                                currentRecurringExpense: recurringExpense
                            }
                        });
                    }
                }
                break;
            case this.match('/drawer/transactions', pathname):
                {
                    const searchParams = new URL(window.location.href).searchParams;
                    const startDate: LocalDate = searchParams.get('start')
                        ? LocalDate.parse(searchParams.get('start'))
                        : undefined;
                    const endDate: LocalDate = searchParams.get('end')
                        ? LocalDate.parse(searchParams.get('end'))
                        : undefined;
                    const filters = {
                        account: searchParams.get('account'),
                        incomeSource: searchParams.get('incomesource'),
                        group: searchParams.get('group'),
                        page: searchParams.get('page'),
                        search: searchParams.get('search'),
                        selections: searchParams.get('selections'),
                        state: 'unified-transactions',
                        type: searchParams.get('type')
                    };
                    await this.component.transactionFactory.openTransactionsRoute(startDate, endDate, {
                        filters
                    });
                }
                break;
            case this.match('/drawer/income/:string/:date/:date', pathname):
                {
                    const params = pathname.split('/');
                    const startDate: LocalDate = LocalDate.parse(params[4]);
                    const endDate: LocalDate = LocalDate.parse(params[5]);
                    await this.component.transactionFactory.openTransactionsRoute(startDate, endDate, {
                        filters: { incomeSource: params[3] }
                    });
                }
                break;
            case this.match('/drawer/account/:string', pathname):
                {
                    const accountId: string = pathname.split('/').pop();
                    const state: string = 'account';
                    let account: AccountModel = _.find(this.component.props.user.accounts, { accountId });
                    const hiddenAccounts: AccountModel[] = await AccountService.hiddenAccounts();
                    if (!account) {
                        account = _.find(hiddenAccounts, { accountId });
                    }
                    const searchParams = new URL(window.location.href).searchParams;
                    const startDate: LocalDate = searchParams.get('start')
                        ? LocalDate.parse(searchParams.get('start'))
                        : undefined;
                    const endDate: LocalDate = searchParams.get('end')
                        ? LocalDate.parse(searchParams.get('end'))
                        : undefined;

                    const filters = {
                        account: account.id,
                        currentAccount: account,
                        incomeSource: searchParams.get('incomesource'),
                        hiddenAccounts,
                        group: searchParams.get('group'),
                        search: searchParams.get('search'),
                        selections: searchParams.get('selections'),
                        state: 'account',
                        type: searchParams.get('type')
                    };

                    await this.component.transactionFactory.openTransactionsRoute(
                        startDate,
                        endDate,
                        {
                            filters
                        },
                        state
                    );
                }
                break;
            case this.match('/review/expenses/:boolean', pathname):
                {
                    const filters: any = {
                        review: true,
                        type: 'TRANSACTION_EXPENSE',
                        mentor: true
                    };
                    await this.component.transactionFactory.openTransactionsRoute(null, null, {
                        filters
                    });
                }
                break;
            case this.match('/review/income/:boolean', pathname):
                {
                    const filters: any = {
                        review: true,
                        type: 'TRANSACTION_INCOME',
                        mentor: true
                    };
                    await this.component.transactionFactory.openTransactionsRoute(null, null, {
                        filters
                    });
                }
                break;
            case this.match('/review', pathname):
                {
                    const filters: any = {
                        review: true
                    };
                    await this.component.transactionFactory.openTransactionsRoute(null, null, {
                        filters
                    });
                }
                break;
            case this.match('/income/new', pathname):
                {
                    const currentIncomeSource: IncomeSourceModel = new IncomeSourceModel();
                    currentIncomeSource.annualizedIncome = 0;
                    currentIncomeSource.name = '';

                    this.component.setState({
                        drawerState: 'income',
                        drawerProps: {
                            currentIncomeSource
                        }
                    });
                }
                break;
            case this.match('/income/:string', pathname):
                {
                    const incomeUid: string = pathname.split('/').pop();
                    const currentIncomeSource = await IncomeSourceService.getIncomeSource(incomeUid);

                    if (currentIncomeSource) {
                        this.component.setState({
                            drawerState: 'income',
                            drawerProps: {
                                currentIncomeSource
                            }
                        });
                    }
                }
                break;
            case this.match('drawer/:string', pathname):
                {
                    const drawerName: string = pathname.split('/').pop();
                    switch (drawerName) {
                        case 'match':
                            {
                                const incomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();
                                const match: MatchModel = await MatchService.create();

                                this.component.setState({
                                    drawerState: drawerName,
                                    drawerProps: {
                                        user: this.component.props.user,
                                        incomeSources,
                                        match,
                                        handleRefresh: this.component.handleReloadUser.bind(this.component)
                                    }
                                });
                            }
                            break;
                        case 'income-rules':
                            {
                                const incomeRules = await IncomeSourceRuleService.getRules();
                                const incomeSources = await IncomeSourceService.getIncomeSources();

                                this.component.setState({
                                    drawerState: drawerName,
                                    drawerProps: {
                                        incomeRules,
                                        incomeSources,
                                        user: this.component.props.user,
                                        handleRefresh: this.component.handleReloadUser.bind(this.component),
                                        handleBack: async () =>
                                            await NavigationService.goTo(this.component.props.history, '/')
                                    }
                                });
                            }
                            break;
                        case 'rules':
                            {
                                const rules: RuleModel[] = await RuleService.getRules();
                                const incomeRules = await IncomeSourceRuleService.getRules();
                                const categories: CategoryModel[] = await CategoryService.getCategories();
                                const incomeSources = await IncomeSourceService.getIncomeSources();

                                this.component.setState({
                                    drawerState: drawerName,
                                    drawerProps: {
                                        rules,
                                        incomeRules,
                                        categories,
                                        incomeSources,
                                        user: this.component.props.user,
                                        handleRefresh: this.component.handleReloadUser.bind(this.component),
                                        handleBack: async () =>
                                            await NavigationService.goTo(this.component.props.history, '/')
                                    }
                                });
                            }
                            break;
                        case 'account':
                            this.component.setState({
                                drawerState: drawerName,
                                drawerProps: {
                                    hiddenAccounts: await AccountService.hiddenAccounts()
                                }
                            });
                            break;
                        case 'accounts':
                            this.component.setState({
                                drawerState: drawerName,
                                drawerProps: {
                                    hiddenAccounts: await AccountService.hiddenAccounts()
                                }
                            });
                            break;
                        case 'profile':
                            this.component.setState({
                                drawerState: drawerName,
                                drawerProps: {
                                    hiddenAccounts: await AccountService.hiddenAccounts(),
                                    accounts: await AccountService.getAccounts(),
                                    persons: PersonService.getPersonsFromUser(this.component.props.user)
                                }
                            });
                            break;
                        default:
                            this.component.drawerFactory.setDrawerRoute(drawerName);
                    }
                }
                break;

            case pathname === '/':
                this.component.setState({
                    drawerState: 'start',
                    drawerProps: {
                        summary: await BudgetService.getSummary(),
                        handleBack: async () =>
                            await NavigationService.goTo(this.component.props.history, '/', 'Top drawer'),
                        persons: PersonService.getPersonsFromUser(this.component.props.user)
                    }
                });
                window.dispatchEvent(new Event('resize'));
                break;
        }
    }

    private getDateString(month: boolean, startDate: LocalDate, age: number): string {
        let dateString: string;
        if (month) {
            dateString = `${FormatService.formatMonth(startDate)} ${startDate.year()}, Age ${age}`;
        } else {
            dateString = `${startDate.year()}, Age ${age}-${age + 1}`;
        }

        return dateString;
    }

    private async showAssetDebtReport(phrases: string[], month: boolean): Promise<void> {
        const cashFlowIndex: number = Number(phrases[phrases.length - 1]);
        const zoom: string = month ? 'month' : 'year';
        const cashFlows = month ? this.component.props.user.rawCashFlow : this.component.props.user.cashFlow;
        const startDate: LocalDate = cashFlows[cashFlowIndex].startDate;
        const endDate: LocalDate = cashFlows[cashFlowIndex].endDate;
        const age: number = CashFlowService.getAgeAtCashFlow(this.component.props.user, cashFlows[cashFlowIndex]);
        const cashFlowDetails: any = await CashFlowService.getCashflowDetails('assetDebt', zoom, startDate);
        const netWorth: number = cashFlowDetails.assets.amount - cashFlowDetails.debts.amount;
        let offset: number;
        let freeCash: number;
        if (month) {
            offset = CashFlowService.getCashFlowIndexWithDate(this.component.props.user.rawCashFlow, startDate);
            freeCash = this.component.props.user.rawCashFlow[offset].freeCash;
        } else {
            offset = CashFlowService.getCashFlowIndexWithDate(this.component.props.user.cashFlow, startDate);
            freeCash = this.component.props.user.cashFlow[offset].freeCash;
        }
        const liquidAssets: any[] = cashFlowDetails.assets.bases.filter((asset: any) => asset.liquid);
        const restrictedAssets: any[] = cashFlowDetails.assets.bases.filter((asset: any) => !asset.liquid);
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const liquidAssetsTotal: number = liquidAssets.reduce((a, b) => +a + +b.amount, 0) + freeCash;
        const restrictedAssetsTotal: number = restrictedAssets.reduce((a, b) => +a + +b.amount, 0);
        const period: TimePeriod = TimeService.getTimePeriodForRange(startDate, endDate);
        const dateString: string = this.getDateString(month, startDate, age);
        const cards: CardModel[] = _.get(this, 'component.props.user.cards');

        this.component.drawerFactory.setNewDrawerRouteState('report-asset-debt', {
            user: this.component.props.user,
            handleRefresh: this.component.handleReloadUser.bind(this.component),
            handleBack: async () => await NavigationService.goTo(this.component.props.history, '/'),
            handleEditDebt: this.component.handleEditDebt.bind(this.component),
            handleEditInvestment: this.component.handleEditInvestment.bind(this.component),
            history: this.component.props.history,
            cashFlowDetails,
            startDate,
            endDate,
            age,
            netWorth,
            liquidAssets,
            restrictedAssets,
            liquidAssetsTotal,
            restrictedAssetsTotal,
            period,
            freeCash,
            dateString,
            offset,
            cards
        });
    }

    private async showNetworth(phrases: string[], month: boolean): Promise<void> {
        const cashFlowIndex: number = Number(phrases[phrases.length - 1]);
        const zoom: string = month ? 'month' : 'year';
        const cashFlows = month ? this.component.props.user.rawCashFlow : this.component.props.user.cashFlow;
        const startDate: LocalDate = cashFlows[cashFlowIndex].startDate;
        const endDate: LocalDate = cashFlows[cashFlowIndex].endDate;
        const age: number = CashFlowService.getAgeAtCashFlow(this.component.props.user, cashFlows[cashFlowIndex]);
        const cashFlowDetails: any = await CashFlowService.getCashflowDetails('assetDebt', zoom, startDate);
        const netWorth: number = cashFlowDetails.assets.amount - cashFlowDetails.debts.amount;
        const liquidAssets: any[] = cashFlowDetails.assets.bases.filter((asset: any) => asset.liquid);
        const restrictedAssets: any[] = cashFlowDetails.assets.bases.filter((asset: any) => !asset.liquid);
        const liquidAssetsTotal: number = liquidAssets.reduce((a, b) => +a + +b.amount, 0);
        const restrictedAssetsTotal: number = restrictedAssets.reduce((a, b) => +a + +b.amount, 0);
        const period: TimePeriod = TimeService.getTimePeriodForRange(startDate, endDate);
        const dateString: string = this.getDateString(month, startDate, age);

        this.component.drawerFactory.setNewDrawerRouteState('show-work-networth', {
            user: this.component.props.user,
            handleRefresh: this.component.handleReloadUser.bind(this.component),
            handleBack: async () => await NavigationService.goTo(this.component.props.history, '/'),
            cashFlowIndex,
            cashFlowDetails,
            startDate,
            endDate,
            age,
            netWorth,
            liquidAssets,
            restrictedAssets,
            liquidAssetsTotal,
            restrictedAssetsTotal,
            period,
            dateString
        });
    }

    private async showIncomeExpenseReport(phrases: string[], month: boolean): Promise<void> {
        const cashFlowIndex: number = Number(phrases[phrases.length - 1]);
        // null and type check
        if (isNaN(cashFlowIndex)) {
            await NavigationService.goTo(this.component.props.history, '/');
            return;
        }

        if (!this.component.props.user) await this.component.handleReloadUser();
        const cashFlows = month ? this.component.props.user.rawCashFlow : this.component.props.user.cashFlow;
        const rawCashFlows = this.component.props.user.rawCashFlow;
        // null check
        if (!cashFlows[cashFlowIndex]) {
            await NavigationService.goTo(this.component.props.history, '/');
            return;
        }

        const startDate: LocalDate = cashFlows[cashFlowIndex].startDate;
        const endDate: LocalDate = cashFlows[cashFlowIndex].endDate;
        const zoom: string = month ? 'month' : 'year';

        /**
         * First we call each of the async functions and hold onto their promises (var names ending in P)
         * Then we set their actual values with a call to Promise.all() which we await.
         * This allows all of these functions (which include api calls) to run in parallel and we collect
         * their values when they are all done.
         */
        const incomeSourcesP: Promise<IncomeSourceModel[]> = IncomeSourceService.getIncomeSources();
        const cashFlowDetailsP: Promise<any> = CashFlowService.getCashflowDetails('incomeExpense', zoom, startDate);
        const categoriesP: Promise<CategoryModel[]> = CategoryService.getCategories();
        const transactionsP: Promise<TransactionModel[]> = TransactionService.getTransactions(startDate, endDate);
        const accountsP: Promise<AccountModel[]> = AccountService.getAccounts();
        const budgetsP: Promise<BudgetModel[]> = BudgetService.getBudget();

        // Set the consts to their values in the array returned by Promise.all()
        const [incomeSources, cashFlowDetails, categories, transactions, accounts, budgets]: [
            IncomeSourceModel[],
            any,
            CategoryModel[],
            TransactionModel[],
            AccountModel[],
            BudgetModel[]
        ] = await Promise.all([incomeSourcesP, cashFlowDetailsP, categoriesP, transactionsP, accountsP, budgetsP]);

        // this should be happening automatically on the BE
        // BudgetService.updateCurrentlySpent(budgets, startDate, endDate);

        const delta: number = cashFlowDetails.incomes.amount - cashFlowDetails.expenses.amount;
        const topDeposits: TransactionModel[] = transactions
            .filter((tr: TransactionModel) => tr.group === 'Income & Deposits')
            .sort((a: TransactionModel, b: TransactionModel) => (a.amount > b.amount ? 1 : -1))
            .slice(0, 3)
            .map((tr: TransactionModel) => {
                tr.amount = Math.abs(tr.amount);
                return tr;
            });
        const topExpenses: TransactionModel[] = transactions
            .filter((tr: TransactionModel) => tr.group !== 'Income & Deposits')
            .sort((a: TransactionModel, b: TransactionModel) => (a.amount < b.amount ? 1 : -1))
            .slice(0, 3);
        const plotPoints: PlotPoint[] = ReportService.getPlotPoints(rawCashFlows, startDate, endDate);
        const age: number = CashFlowService.getAgeAtCashFlow(this.component.props.user, cashFlows[cashFlowIndex]);
        const period: TimePeriod = TimeService.getTimePeriodForRange(startDate, endDate);
        const cards: CardModel[] = _.get(this, 'component.props.user.cards');
        const dateString: string = this.getDateString(month, startDate, age);

        // add category information to bases for convenience
        cashFlowDetails.expenses.bases = cashFlowDetails.expenses.bases.map((base: any) => {
            let parentCategory: CategoryModel;
            try {
                const relevantCat: CategoryModel = categories.find(
                    (category: CategoryModel) => category.id === base.groupUid
                );
                base.category = relevantCat;
                if (relevantCat?.parentId) {
                    base.category = relevantCat;
                    if (relevantCat.parentId) {
                        parentCategory = categories.find((category) => category.id === relevantCat.parentId);
                        if (parentCategory) {
                            base.parentCategory = parentCategory;
                        }
                    }
                }
            } catch (e) {
                console.error(e);
            }

            return base;
        });

        // group by parent expense category
        let groupedExpenses: any[] = _(cashFlowDetails.expenses.bases)
            // group by parent category name
            .groupBy((item) => _.get(item, 'parentCategory.name', item.category.name))
            // structure the individual groups
            .map((children, key) => ({
                id: _.get(
                    children,
                    '[0].parentCategory.id',
                    categories.find((cat: CategoryModel) => cat.name === key)
                ),
                name: key,
                children: _(children)
                    // group children by name
                    .groupBy('name')
                    // remove dupes, sum amount
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    .map((g) => _.mergeWith(...g, (o: any, s: any, k: any) => (k === 'amount' ? o + s : o)))
                    .value(),
                // sum children amounts for parent amount
                amount: _.sumBy(children, (child) => child.amount)
            }))
            .map((child: any) => {
                // TODO: Do this cleanup for Uncategorised some better way
                const nestedId: string = _.get(child, 'id.id');
                if (nestedId) child.id = nestedId;
                return child;
            })
            .value();

        const adjustments: any[] = _.get(cashFlowDetails, 'expenses.adjustments');

        if (adjustments?.length)
            groupedExpenses = _(adjustments)
                .concat(groupedExpenses)
                .orderBy((ge) => ge.amount, ['desc'])
                .value();

        const expenseTotal = _(groupedExpenses).sumBy((ge) => ge.amount);

        this.component.drawerFactory.setNewDrawerRouteState('report-income-expense', {
            user: this.component.props.user,
            handleRefresh: this.component.handleReloadUser.bind(this.component),
            handleBack: async () => await NavigationService.goTo(this.component.props.history, '/'),
            incomeSources,
            cashFlowDetails,
            categories,
            zoom,
            startDate,
            endDate,
            cashFlow: cashFlows[cashFlowIndex],
            cashFlows,
            transactions,
            plotPoints,
            age,
            delta,
            topDeposits,
            topExpenses,
            accounts,
            period,
            budgets,
            groupedExpenses,
            cards,
            dateString,
            expenseTotal
        });
    }

    private async handleShowReport(pathname: string, month: boolean = true): Promise<void> {
        const phrases: string[] = pathname.split('/');
        switch (phrases[phrases.length - 2]) {
            case 'incomes':
            case 'expenses':
                return await this.showIncomeExpenseReport(phrases, month);
            case 'assets':
            case 'debts':
                return await this.showAssetDebtReport(phrases, month);
            case 'networth':
                return await this.showNetworth(phrases, month);
            default:
                return await this.showIncomeExpenseReport(phrases, month);
        }
    }
}
